import type { ScannerModel } from "../state/scannerMachine";
import type { Language } from "../i18n";
import { getFriendlyRpcErrorMessage, t } from "../i18n";
import type { RecognitionMode } from "./RecognitionModeSwitch";
import type { PriceTagProductCandidate } from "../types";

export interface OverlayProps {
  model: ScannerModel;
  onRetryCamera?: () => void;
  onManualEntry?: () => void;
  onFavorite?: () => void;
  onBack?: () => void;
  onClose?: () => void;
  onSelectCandidate?: (candidate: PriceTagProductCandidate) => void;
  isFavorited?: boolean;
  isFavoriteBusy?: boolean;
  language?: Language;
  recognitionMode?: RecognitionMode;
}

/** Renders the current comparison result, or a status message, on top of the camera preview (spec §29, §33). */
export function Overlay({
  model,
  onRetryCamera,
  onManualEntry,
  onFavorite,
  onBack,
  onClose,
  onSelectCandidate,
  isFavorited = false,
  isFavoriteBusy = false,
  language = "ru",
  recognitionMode = "barcode",
}: OverlayProps) {
  if (model.state === "CONFIRM" && model.candidates && model.candidates.length > 0) {
    return (
      <div className="overlay overlay--result scan-sheet" data-testid="price-tag-candidates">
        <div className="overlay__product">{t(language, "price_tag_confirm_title")}</div>
        <p className="overlay__missing">{t(language, "price_tag_confirm_hint")}</p>
        <ul className="overlay__candidates">
          {model.candidates.map((candidate) => (
            <li key={candidate.productId}>
              <button
                type="button"
                className="overlay__candidate"
                onClick={() => onSelectCandidate?.(candidate)}
                data-testid="price-tag-candidate"
              >
                <span>{candidate.name}</span>
                {candidate.size ? <small>{candidate.size}</small> : null}
              </button>
            </li>
          ))}
        </ul>
        {onClose && (
          <button type="button" className="overlay__back-link" onClick={onClose}>
            {t(language, "price_tag_retry")}
          </button>
        )}
      </div>
    );
  }

  if (model.state === "RESULT" && model.result) {
    const { product, prices, best } = model.result;
    const current = prices.find((p) => p.price === Math.max(...prices.map((x) => x.price))) ?? prices[0];
    const saving = current ? current.price - best.price : null;

    const thumb =
      typeof product.thumbnail === "string" && product.thumbnail.startsWith("data:image/")
        ? product.thumbnail
        : null;

    return (
      <div className="overlay overlay--result scan-sheet">
        <div className="overlay__header">
          <div className="overlay__result-main">
            {thumb && (
              <img
                src={thumb}
                alt=""
                width={80}
                height={80}
                className="overlay__thumb"
                data-testid="overlay-thumb"
              />
            )}
            <div className="overlay__product">{product.name}</div>
          </div>
          {(onFavorite || onClose) && (
            <div className="overlay__actions">
              {onFavorite && (
                <button
                  type="button"
                  className={`overlay__favorite ${isFavorited ? "overlay__favorite--on" : ""}`}
                  onClick={onFavorite}
                  disabled={isFavoriteBusy}
                  aria-pressed={isFavorited}
                  aria-label={t(language, isFavorited ? "overlay_favorite_remove_aria" : "overlay_favorite_add_aria")}
                  data-testid="overlay-favorite"
                >
                  {isFavorited ? "♥" : "♡"}
                </button>
              )}
              {onClose && (
                <button
                  type="button"
                  className="overlay__close"
                  onClick={onClose}
                  aria-label={t(language, "overlay_close_aria")}
                  data-testid="overlay-close"
                >
                  ✕
                </button>
              )}
            </div>
          )}
        </div>
        {model.result.missing ? (
          <p className="overlay__missing" data-testid="overlay-missing">
            {t(language, "overlay_missing_body")}
          </p>
        ) : (
          <>
            <ul className="overlay__prices">
              {prices.map((p) => (
                <li key={p.storeId} className={p.storeId === best.storeId ? "overlay__price overlay__price--best" : "overlay__price"}>
                  <span>{p.storeName}</span>
                  <span>
                    {p.price.toFixed(2)} {p.currency}
                  </span>
                </li>
              ))}
            </ul>
            <div className="overlay__best">{t(language, "overlay_best_price", { price: best.price.toFixed(2) })}</div>
            {saving !== null && saving > 0 && <div className="overlay__save">{t(language, "overlay_save", { saving: saving.toFixed(2) })}</div>}
          </>
        )}
        {onBack && (
          <button
            type="button"
            className="overlay__back-link"
            onClick={onBack}
            data-testid="overlay-back"
          >
            {t(language, "overlay_back_link")}
          </button>
        )}
      </div>
    );
  }

  if (model.state === "CAMERA_PERMISSION_DENIED") {
    return (
      <div className="overlay overlay--camera-error">
        <div className="camera-error">
          <div className="camera-error__icon">📷</div>
          <h2 className="camera-error__title">{t(language, "cam_perm_title")}</h2>
          <p className="camera-error__message">
            {t(language, "cam_perm_msg")}
          </p>
          <div className="camera-error__actions">
            <button type="button" className="camera-error__button camera-error__button--primary" onClick={onRetryCamera}>
              {t(language, "cam_retry_btn")}
            </button>
            <button type="button" className="camera-error__button camera-error__button--secondary" onClick={onManualEntry}>
              {t(language, "cam_manual_btn")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (model.state === "CAMERA_NOT_FOUND") {
    return (
      <div className="overlay overlay--camera-error">
        <div className="camera-error">
          <div className="camera-error__icon">📱</div>
          <h2 className="camera-error__title">{t(language, "cam_not_found_title")}</h2>
          <p className="camera-error__message">
            {t(language, "cam_not_found_msg")}
          </p>
          <div className="camera-error__actions">
            <button type="button" className="camera-error__button camera-error__button--primary" onClick={onRetryCamera}>
              {t(language, "cam_retry_btn")}
            </button>
            <button type="button" className="camera-error__button camera-error__button--secondary" onClick={onManualEntry}>
              {t(language, "cam_manual_btn")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (model.state === "ERROR" || model.state === "AUTH_ERROR") {
    const errorText = model.errorMessage
      ? getFriendlyRpcErrorMessage(model.errorMessage, language, model.errorMessage)
      : t(language, "overlay_generic_error");
    return <div className="overlay overlay--error">{errorText}</div>;
  }

  const stateKeyMap: Partial<Record<ScannerModel["state"], Parameters<typeof t>[1]>> = {
    AUTHENTICATING: "state_authenticating",
    CAMERA_READY: "state_camera_ready",
    SCANNING: "state_scanning",
    DETECTED: "state_detected",
    LOOKUP: "state_lookup",
    NOT_FOUND: "state_not_found",
    OFFLINE: "state_offline",
    SESSION_EXPIRED: "state_session_expired",
  };

  const key = stateKeyMap[model.state];
  if (!key) return null;
  const statusKey =
    recognitionMode === "priceTag" && model.state === "SCANNING"
      ? "state_scanning_tag"
      : recognitionMode === "priceTag" && model.state === "LOOKUP"
        ? "state_lookup_tag"
        : recognitionMode === "priceTag" && model.state === "NOT_FOUND"
          ? "state_not_found_tag"
          : key;
  return <div className="overlay overlay--status">{t(language, statusKey)}</div>;
}
